import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { usePaymentRequests, useConfirmPayment, useRejectPaymentRequest } from '@/hooks/finance/usePaymentRequests';
import { PaymentRequest } from '@/types/database/payment-requests';
import { supabase } from '@/integrations/supabase/client';
import { format, isWithinInterval, parseISO } from 'date-fns';
import { CheckCircle, FileText, Building, Calendar, DollarSign, User, Filter, ArrowUpDown, X, UserCircle, ShieldCheck, XCircle } from 'lucide-react';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { BUAllocationEditor, AllocationValue, allocationsAreValid } from '@/components/finance/BUAllocationEditor';
import { usePaymentRequestAllocations, useSavePaymentRequestAllocations } from '@/hooks/finance/usePaymentRequestAllocations';
import { DEFAULT_BUSINESS_UNIT } from '@/constants/businessUnits';
import { useVatRate, useActiveWithholdingRates } from '@/hooks/finance/useTaxSettings';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { formatCurrency } from '@/lib/currency';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

type SortOption = 'date_asc' | 'date_desc' | 'amount_asc' | 'amount_desc' | 'created_asc' | 'created_desc';
type VatMode = 'none' | 'add' | 'deduct';

const PaymentRequestConfirmationList = () => {
  const { data: approvedRequests, isLoading, error } = usePaymentRequests('approved');
  const confirmPayment = useConfirmPayment();
  const rejectPayment = useRejectPaymentRequest();
  const { data: bankAccounts, isLoading: bankAccountsLoading } = useBankAccounts(true);
  const { data: defaultVatRate = 15 } = useVatRate();
  const { data: withholdingRates = [] } = useActiveWithholdingRates();
  
  const [selectedRequest, setSelectedRequest] = useState<PaymentRequest | null>(null);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentReference, setPaymentReference] = useState('');
  const [paidFromAccountId, setPaidFromAccountId] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [editAllocations, setEditAllocations] = useState<AllocationValue[]>([]);
  const { data: loadedAllocations } = usePaymentRequestAllocations(selectedRequest?.id);
  const saveAllocations = useSavePaymentRequestAllocations();
  const [approverNames, setApproverNames] = useState<Record<string, string>>({});
  
  // VAT and Withholding state
  const [vatMode, setVatMode] = useState<VatMode>('none');
  const [withholdingApplicable, setWithholdingApplicable] = useState(false);
  const [selectedWithholdingRateId, setSelectedWithholdingRateId] = useState('');
  
  // Filter states
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date_asc');

  // Fetch approver names
  useEffect(() => {
    const fetchApproverNames = async () => {
      if (!approvedRequests) return;
      
      const approverIds = new Set<string>();
      approvedRequests.forEach(req => {
        req.approvals?.forEach(approval => {
          if (approval.approver_id && approval.status === 'approved') {
            approverIds.add(approval.approver_id);
          }
        });
      });
      
      if (approverIds.size === 0) return;
      
      const { data } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name')
        .in('id', Array.from(approverIds));
      
      if (data) {
        const names: Record<string, string> = {};
        data.forEach(user => {
          names[user.id] = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown';
        });
        setApproverNames(names);
      }
    };
    
    fetchApproverNames();
  }, [approvedRequests]);

  // Sync loaded allocations into editor when opening dialog
  useEffect(() => {
    if (!selectedRequest) return;
    if (loadedAllocations && loadedAllocations.length > 0) {
      setEditAllocations(loadedAllocations);
    } else {
      const bu = ((selectedRequest as any).business_unit as string) || DEFAULT_BUSINESS_UNIT;
      setEditAllocations([{ business_unit: bu as any, percentage: 100 }]);
    }
  }, [selectedRequest, loadedAllocations]);

  // Extract unique departments and categories
  const { departments, categories } = useMemo(() => {
    if (!approvedRequests) return { departments: [], categories: [] };
    
    const deptMap = new Map<string, string>();
    const catMap = new Map<string, string>();
    
    approvedRequests.forEach(req => {
      if (req.department?.id && req.department?.name) {
        deptMap.set(req.department.id, req.department.name);
      }
      if (req.category?.id && req.category?.name) {
        catMap.set(req.category.id, req.category.name);
      }
    });
    
    return {
      departments: Array.from(deptMap, ([id, name]) => ({ id, name })),
      categories: Array.from(catMap, ([id, name]) => ({ id, name })),
    };
  }, [approvedRequests]);

  // Filter and sort requests
  const filteredRequests = useMemo(() => {
    if (!approvedRequests) return [];
    
    let filtered = [...approvedRequests];
    
    // Apply department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(req => req.department?.id === departmentFilter);
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(req => req.category?.id === categoryFilter);
    }
    
    // Apply date range filter
    if (dateFrom || dateTo) {
      filtered = filtered.filter(req => {
        if (!req.preferred_payment_date) return false;
        const reqDate = parseISO(req.preferred_payment_date);
        
        if (dateFrom && dateTo) {
          return isWithinInterval(reqDate, {
            start: parseISO(dateFrom),
            end: parseISO(dateTo),
          });
        } else if (dateFrom) {
          return reqDate >= parseISO(dateFrom);
        } else if (dateTo) {
          return reqDate <= parseISO(dateTo);
        }
        return true;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':
          return (a.preferred_payment_date || '').localeCompare(b.preferred_payment_date || '');
        case 'date_desc':
          return (b.preferred_payment_date || '').localeCompare(a.preferred_payment_date || '');
        case 'amount_asc':
          return (a.total_amount || 0) - (b.total_amount || 0);
        case 'amount_desc':
          return (b.total_amount || 0) - (a.total_amount || 0);
        case 'created_asc':
          return (a.created_at || '').localeCompare(b.created_at || '');
        case 'created_desc':
          return (b.created_at || '').localeCompare(a.created_at || '');
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [approvedRequests, departmentFilter, categoryFilter, dateFrom, dateTo, sortBy]);

  const clearFilters = () => {
    setDepartmentFilter('all');
    setCategoryFilter('all');
    setDateFrom('');
    setDateTo('');
    setSortBy('date_asc');
  };

  const hasActiveFilters = departmentFilter !== 'all' || categoryFilter !== 'all' || dateFrom || dateTo;

  const handleOpenConfirmDialog = (request: PaymentRequest) => {
    setSelectedRequest(request);
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentReference('');
    setPaidFromAccountId('');
    setVatMode('none');
    setWithholdingApplicable(false);
    setSelectedWithholdingRateId('');
    setIsDialogOpen(true);
  };

  const handleOpenRejectDialog = (request: PaymentRequest) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setIsRejectDialogOpen(true);
  };

  const handleRejectPayment = async () => {
    if (!selectedRequest || !rejectionReason) return;
    
    await rejectPayment.mutateAsync({
      id: selectedRequest.id,
      reason: rejectionReason,
    });
    
    setIsRejectDialogOpen(false);
    setSelectedRequest(null);
    setRejectionReason('');
  };

  // Calculate payment totals based on VAT mode and withholding
  const paymentTotals = useMemo(() => {
    const requestAmount = selectedRequest?.total_amount || 0;
    const vatRateDecimal = defaultVatRate / 100;
    
    let baseAmount: number;
    let vatAmount: number;
    let subtotal: number;
    
    switch (vatMode) {
      case 'add':
        baseAmount = requestAmount;
        vatAmount = baseAmount * vatRateDecimal;
        subtotal = baseAmount + vatAmount;
        break;
      case 'deduct':
        baseAmount = requestAmount / (1 + vatRateDecimal);
        vatAmount = requestAmount - baseAmount;
        subtotal = requestAmount;
        break;
      default:
        baseAmount = requestAmount;
        vatAmount = 0;
        subtotal = requestAmount;
    }
    
    const withholdingRate = withholdingApplicable && selectedWithholdingRateId
      ? withholdingRates.find(r => r.id === selectedWithholdingRateId)?.rate || 0
      : 0;
    const withholdingAmount = baseAmount * (withholdingRate / 100);
    const netPayment = subtotal - withholdingAmount;
    
    return {
      baseAmount,
      vatRate: vatMode !== 'none' ? defaultVatRate : 0,
      vatAmount,
      subtotal,
      withholdingRate,
      withholdingAmount,
      netPayment
    };
  }, [selectedRequest?.total_amount, vatMode, defaultVatRate, withholdingApplicable, selectedWithholdingRateId, withholdingRates]);

  const handleConfirmPayment = async () => {
    if (!selectedRequest || !paidFromAccountId || !paymentReference.trim()) return;
    if (!allocationsAreValid(editAllocations) || editAllocations.length === 0) {
      return;
    }

    // Save allocations first so confirmPayment copies them into the expense
    try {
      await saveAllocations.mutateAsync({
        paymentRequestId: selectedRequest.id,
        allocations: editAllocations,
      });
    } catch {
      return;
    }

    
    await confirmPayment.mutateAsync({
      id: selectedRequest.id,
      paymentReference: paymentReference.trim(),
      actualPaymentDate: paymentDate,
      bankAccountId: paidFromAccountId,
      baseAmount: paymentTotals.baseAmount,
      vatRate: paymentTotals.vatRate,
      vatAmount: paymentTotals.vatAmount,
      totalExpense: paymentTotals.subtotal,
      withholdingRate: paymentTotals.withholdingRate,
      withholdingAmount: paymentTotals.withholdingAmount,
      netPayment: paymentTotals.netPayment,
    });
    
    setIsDialogOpen(false);
    setSelectedRequest(null);
    setPaidFromAccountId('');
    setVatMode('none');
    setWithholdingApplicable(false);
    setSelectedWithholdingRateId('');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          Failed to load approved payment requests
        </CardContent>
      </Card>
    );
  }

  if (!approvedRequests || approvedRequests.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium text-foreground">No pending payments</h3>
          <p className="mt-2 text-muted-foreground">
            All approved payment requests have been processed.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Filters and Sorting */}
      <Card className="mb-4">
        <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="gap-2 p-0 hover:bg-transparent">
                  <Filter className="h-4 w-4" />
                  <span className="font-medium">Filters & Sorting</span>
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="ml-2">
                      Active
                    </Badge>
                  )}
                </Button>
              </CollapsibleTrigger>
              
              <div className="flex items-center gap-2">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                  <SelectTrigger className="w-[180px]">
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date_asc">Due Date (Earliest)</SelectItem>
                    <SelectItem value="date_desc">Due Date (Latest)</SelectItem>
                    <SelectItem value="amount_desc">Amount (Highest)</SelectItem>
                    <SelectItem value="amount_asc">Amount (Lowest)</SelectItem>
                    <SelectItem value="created_desc">Newest First</SelectItem>
                    <SelectItem value="created_asc">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          
          <CollapsibleContent>
            <CardContent className="pt-2">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Due Date From</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Due Date To</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>
              
              {hasActiveFilters && (
                <div className="mt-4 flex justify-end">
                  <Button variant="outline" size="sm" onClick={clearFilters} className="gap-2">
                    <X className="h-4 w-4" />
                    Clear Filters
                  </Button>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Results count */}
      <div className="mb-4 text-sm text-muted-foreground">
        Showing {filteredRequests.length} of {approvedRequests.length} payment requests
      </div>

      {/* Request list */}
      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Filter className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-medium text-foreground">No matching requests</h3>
            <p className="mt-2 text-muted-foreground">
              Try adjusting your filters to see more results.
            </p>
            <Button variant="outline" className="mt-4" onClick={clearFilters}>
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredRequests.map((request) => {
            const approvedApproval = request.approvals?.find(a => a.status === 'approved');
            const approverName = approvedApproval?.approver_id ? approverNames[approvedApproval.approver_id] : null;
            
            return (
              <Card key={request.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between gap-4">
                    {/* Left: Request info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="font-mono text-xs">
                          {request.request_number}
                        </Badge>
                        <span className="font-medium text-foreground truncate">{request.title}</span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {(request.requester_first_name || request.requester_last_name) && (
                          <span className="flex items-center gap-1">
                            <UserCircle className="h-3 w-3" />
                            <span className="text-muted-foreground/70">Requestor:</span>
                            {`${request.requester_first_name || ''} ${request.requester_last_name || ''}`.trim()}
                          </span>
                        )}
                        
                        {approverName && (
                          <span className="flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3 text-green-600" />
                            <span className="text-muted-foreground/70">Approved by:</span>
                            {approverName}
                          </span>
                        )}
                        
                        {request.vendor_name && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span className="text-muted-foreground/70">Vendor:</span>
                            {request.vendor_name}
                          </span>
                        )}
                        
                        {request.department && (
                          <span className="flex items-center gap-1">
                            <Building className="h-3 w-3" />
                            {request.department.name}
                          </span>
                        )}
                        
                        {request.preferred_payment_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Due: {format(new Date(request.preferred_payment_date), 'MMM d')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Right: Amount and action */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p className="font-semibold text-foreground">
                          {request.currency || 'ETB'} {request.total_amount?.toLocaleString()}
                        </p>
                        {request.vendor_bank_name && (
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {request.vendor_bank_name}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenRejectDialog(request)}
                        className="gap-1 text-destructive hover:text-destructive"
                      >
                        <XCircle className="h-3 w-3" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleOpenConfirmDialog(request)}
                        className="gap-1"
                      >
                        <CheckCircle className="h-3 w-3" />
                        Confirm
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
            <DialogDescription>
              Confirm payment for {selectedRequest?.request_number} - {selectedRequest?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">Requested Amount</p>
              <p className="text-xl font-bold text-foreground">
                {formatCurrency(selectedRequest?.total_amount || 0, selectedRequest?.currency || 'ETB')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment-date">Payment Date</Label>
              <Input
                id="payment-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="paid-from-account">Paid From Account *</Label>
              <Select value={paidFromAccountId} onValueChange={setPaidFromAccountId}>
                <SelectTrigger id="paid-from-account">
                  <SelectValue placeholder={bankAccountsLoading ? "Loading accounts..." : "Select bank account"} />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts?.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name} {account.bank_name ? `(${account.bank_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* VAT Mode Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">VAT Handling</Label>
              <RadioGroup value={vatMode} onValueChange={(v) => setVatMode(v as VatMode)} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="vat-none-bulk" />
                  <Label htmlFor="vat-none-bulk" className="font-normal cursor-pointer">No VAT</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="add" id="vat-add-bulk" />
                  <Label htmlFor="vat-add-bulk" className="font-normal cursor-pointer">
                    Add VAT ({defaultVatRate}%) – amount is base
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="deduct" id="vat-deduct-bulk" />
                  <Label htmlFor="vat-deduct-bulk" className="font-normal cursor-pointer">
                    Deduct VAT ({defaultVatRate}%) – amount includes VAT
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Withholding Tax */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={withholdingApplicable}
                  onCheckedChange={setWithholdingApplicable}
                />
                <Label>Apply Withholding Tax</Label>
              </div>
              {withholdingApplicable && (
                <Select value={selectedWithholdingRateId} onValueChange={setSelectedWithholdingRateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select withholding rate" />
                  </SelectTrigger>
                  <SelectContent>
                    {withholdingRates.map((rate) => (
                      <SelectItem key={rate.id} value={rate.id}>
                        {rate.name} ({rate.rate}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <Separator />

            {/* Payment Summary */}
            <div className="space-y-2 text-sm bg-muted/50 rounded-lg p-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base Amount</span>
                <span>{formatCurrency(paymentTotals.baseAmount, selectedRequest?.currency || 'ETB')}</span>
              </div>
              {vatMode !== 'none' && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">VAT ({defaultVatRate}%)</span>
                  <span className="text-emerald-600 dark:text-emerald-400">+ {formatCurrency(paymentTotals.vatAmount, selectedRequest?.currency || 'ETB')}</span>
                </div>
              )}
              <div className="flex justify-between font-medium pt-1 border-t border-border">
                <span>Total Expense</span>
                <span>{formatCurrency(paymentTotals.subtotal, selectedRequest?.currency || 'ETB')}</span>
              </div>
              {withholdingApplicable && selectedWithholdingRateId && (
                <>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Withholding ({paymentTotals.withholdingRate}%)</span>
                    <span className="text-destructive">- {formatCurrency(paymentTotals.withholdingAmount, selectedRequest?.currency || 'ETB')}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-base pt-1 border-t border-border">
                    <span>Net Payment</span>
                    <span>{formatCurrency(paymentTotals.netPayment, selectedRequest?.currency || 'ETB')}</span>
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2 border-t border-border pt-3">
              <BUAllocationEditor
                value={editAllocations}
                onChange={setEditAllocations}
                compact
              />
              <p className="text-xs text-muted-foreground">
                These allocations will be saved on the payment request and copied to the expense record.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment-reference">Payment Reference <span className="text-destructive">*</span></Label>
              <Textarea
                id="payment-reference"
                placeholder="Enter transaction reference, check number, etc."
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPayment}
              disabled={
                confirmPayment.isPending ||
                saveAllocations.isPending ||
                !paidFromAccountId ||
                !paymentReference.trim() ||
                !allocationsAreValid(editAllocations) ||
                editAllocations.length === 0
              }
            >
              {confirmPayment.isPending ? 'Confirming...' : 'Confirm Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payment Request</DialogTitle>
            <DialogDescription>
              Reject {selectedRequest?.request_number} - {selectedRequest?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">Amount</p>
              <p className="text-xl font-bold text-foreground">
                {selectedRequest?.currency || 'ETB'} {selectedRequest?.total_amount?.toLocaleString()}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Rejection Reason *</Label>
              <Textarea
                id="rejection-reason"
                placeholder="Enter the reason for rejecting this payment request..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectPayment}
              disabled={rejectPayment.isPending || !rejectionReason}
            >
              {rejectPayment.isPending ? 'Rejecting...' : 'Reject Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentRequestConfirmationList;
